/**
 * ElevenLabs Scribe realtime speech-to-text client (browser side).
 *
 * The browser dials ElevenLabs directly — the backend's only involvement is
 * minting the single-use token and composing the URL (see `api/voice.py`), so
 * the model and VAD tuning stay server-owned and a stale bundle can't pin last
 * month's settings.
 *
 * Segmentation is the server's job: the socket is opened with
 * `commit_strategy=vad`, so ElevenLabs decides when a sentence ended and emits
 * a `committed_transcript`. That is what makes the interview hands-free — no
 * push-to-talk, nothing to press at a junction.
 *
 * Reconnection is not optional here. This runs on a phone in a moving car, so
 * the socket *will* drop: a tunnel, a cell handover, or ElevenLabs closing an
 * idle session. Each reconnect needs a fresh token (they're single-use), which
 * is why the constructor takes a token *factory* rather than a URL.
 */

export type ScribeStatus = "connecting" | "open" | "closed";

export interface ScribeHandlers {
  /** Live, unstable text for the current utterance. Overwritten, not appended. */
  onPartial?: (text: string) => void;
  /** A finished utterance. This is the text the interview acts on. */
  onCommitted: (text: string) => void;
  onStatus?: (status: ScribeStatus) => void;
  /** Terminal failure — reconnection has been given up on. */
  onError: (message: string) => void;
}

/** Resolves a fresh, single-use WebSocket URL (token included). */
export type WebSocketUrlFactory = () => Promise<string>;

/**
 * Provider errors that no amount of retrying fixes. Everything else — a dropped
 * socket, an exhausted server, a throttled commit — is treated as transient.
 */
const FATAL_ERRORS: Record<string, string> = {
  auth_error: "Voice authentication failed. Reload the page and try again.",
  quota_exceeded: "The team's speech-to-text quota is used up.",
  unaccepted_terms: "The ElevenLabs terms haven't been accepted for this account.",
  session_time_limit_exceeded: "The voice session hit its time limit. Start it again to continue.",
};

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 8000;

/**
 * How long a socket must stay open before the attempt counter resets.
 *
 * Resetting on `onopen` alone makes the retry budget unspendable: a server
 * that accepts the connection and drops it immediately — an expired token, a
 * rejected audio format, a session limit — produces open/close/open/close
 * forever, each open zeroing the counter. The failure it's meant to bound
 * (give up and tell the user) never arrives, and the phone quietly burns
 * battery and data reconnecting on a drive.
 */
const CONNECTION_STABLE_MS = 5000;

export class ScribeSocket {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private reconnectTimer: number | null = null;
  /** Fires once a connection has held for {@link CONNECTION_STABLE_MS}; resets `attempts`. */
  private stableTimer: number | null = null;
  /** Set by {@link close}; suppresses reconnection and late handler calls. */
  private disposed = false;
  /**
   * Chunks captured while the socket is down. Bounded — see {@link send}. A
   * short gap (a tunnel) is worth replaying; a long one is worth dropping,
   * because delivering a minute of stale audio all at once would transcribe a
   * conversation the user has already moved past.
   */
  private backlog: Int16Array[] = [];

  constructor(
    private readonly urlFactory: WebSocketUrlFactory,
    private readonly sampleRate: number,
    private readonly handlers: ScribeHandlers,
  ) {}

  async connect(): Promise<void> {
    if (this.disposed) return;
    this.handlers.onStatus?.("connecting");

    let url: string;
    try {
      url = await this.urlFactory();
    } catch {
      // Our own backend failed to mint a token. Retryable — the phone may just
      // have lost signal — so this goes through the same backoff as a drop.
      this.scheduleReconnect("Couldn't reach North Star to start voice.");
      return;
    }
    // A `close()` that landed while the token was in flight.
    if (this.disposed) return;

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      if (this.disposed) return;
      // NOT reset here — see CONNECTION_STABLE_MS. A connection has to prove
      // it can hold before it's allowed to refund the retry budget.
      if (this.stableTimer !== null) window.clearTimeout(this.stableTimer);
      this.stableTimer = window.setTimeout(() => {
        this.stableTimer = null;
        this.attempts = 0;
      }, CONNECTION_STABLE_MS);
      this.handlers.onStatus?.("open");
      // Replay whatever was captured while we were down, oldest first.
      const pending = this.backlog;
      this.backlog = [];
      pending.forEach((chunk) => this.send(chunk));
    };

    ws.onmessage = (event) => {
      if (this.disposed) return;
      this.handleMessage(event.data);
    };

    ws.onerror = () => {
      // Browsers deliberately give no detail here (it would be a cross-origin
      // information leak). `onclose` always follows, so reconnection is handled
      // there — this exists only to stop an unhandled error event.
    };

    ws.onclose = () => {
      if (this.disposed) return;
      // Must clear here too, not only in teardownSocket: this path doesn't go
      // through it. Leaving the timer armed would let the *closed* socket's
      // stability deadline fire five seconds later and zero the counter for
      // its replacement — reintroducing the unspendable-budget bug by the
      // back door.
      if (this.stableTimer !== null) {
        window.clearTimeout(this.stableTimer);
        this.stableTimer = null;
      }
      this.ws = null;
      this.handlers.onStatus?.("closed");
      this.scheduleReconnect("Lost the connection to speech recognition.");
    };
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== "string") return;
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }

    const type = msg.message_type;
    if (typeof type !== "string") return;

    if (type === "partial_transcript") {
      const text = typeof msg.text === "string" ? msg.text : "";
      if (text) this.handlers.onPartial?.(text);
      return;
    }
    // `..._with_timestamps` repeats a segment we've already handled; taking
    // both would submit every utterance twice.
    if (type === "committed_transcript") {
      const text = typeof msg.text === "string" ? msg.text.trim() : "";
      if (text) this.handlers.onCommitted(text);
      return;
    }
    // Two envelopes to cover. The documented event reference lists the failure
    // codes as message *types* (`auth_error`, `quota_exceeded`, …), and
    // wavely's production client keys off exactly that. But a generic
    // `{"message_type": "error", "error": "auth_error"}` is the other shape
    // these APIs commonly emit, and reading only one of them turns a fatal
    // error into an invisible reconnect loop against a credential that will
    // never work. Accept both; the semantics stay the same either way.
    const code = type === "error" && typeof msg.error === "string" ? msg.error : type;
    const fatal = FATAL_ERRORS[code];
    if (fatal) {
      this.disposed = true;
      this.teardownSocket();
      this.handlers.onError(fatal);
    }
    // Anything else (`session_started`, `resource_exhausted`, throttling) is
    // either informational or resolves itself on the reconnect that follows the
    // close the server is about to send.
  }

  private scheduleReconnect(failureMessage: string): void {
    if (this.disposed || this.reconnectTimer !== null) return;
    this.attempts += 1;
    if (this.attempts > MAX_RECONNECT_ATTEMPTS) {
      this.disposed = true;
      this.handlers.onError(failureMessage);
      return;
    }
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** (this.attempts - 1), RECONNECT_MAX_MS);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  /**
   * Queue one PCM chunk for transcription.
   *
   * Safe to call at any point in the lifecycle: while disconnected the chunk
   * lands in a bounded backlog and is replayed on reconnect. The caller doesn't
   * have to know whether the socket is up.
   */
  send(chunk: Int16Array): void {
    if (this.disposed) return;
    if (this.ws?.readyState !== WebSocket.OPEN) {
      // ~4 seconds of audio at 64ms a chunk. Past that the oldest is dropped:
      // replaying a long outage would transcribe stale speech into the middle
      // of a later turn, which is worse than losing it.
      if (this.backlog.length >= 64) this.backlog.shift();
      this.backlog.push(chunk);
      return;
    }
    this.ws.send(
      JSON.stringify({
        message_type: "input_audio_chunk",
        audio_base_64: base64FromPcm(chunk),
        // Commits are the server's call — see `commit_strategy=vad`.
        commit: false,
        sample_rate: this.sampleRate,
      }),
    );
  }

  private teardownSocket(): void {
    // Cleared alongside the socket: a pending stability timer belongs to the
    // connection being torn down, and letting it fire would refund the retry
    // budget for a socket that no longer exists.
    if (this.stableTimer !== null) {
      window.clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    // Drop the handlers first: `close()` fires `onclose` synchronously in some
    // browsers, which would otherwise queue a reconnect we don't want.
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
  }

  /** Permanently close. The instance is not reusable afterwards. */
  close(): void {
    this.disposed = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.backlog = [];
    this.teardownSocket();
  }
}

/**
 * Base64-encode PCM samples for the JSON envelope.
 *
 * Chunked through `String.fromCharCode` because spreading a whole typed array
 * into it blows the argument limit on longer buffers — a crash that only shows
 * up once someone talks for a while, which is exactly when you don't want it.
 */
function base64FromPcm(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let binary = "";
  const STRIDE = 0x8000;
  for (let i = 0; i < bytes.length; i += STRIDE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + STRIDE));
  }
  return btoa(binary);
}
